import { NotificationBell } from '@/components/layout/NotificationBell'
import { UserAvatar } from '@/components/shared/UserAvatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dropdown } from '@/components/ui/dropdown'
import { useAuth } from '@/hooks/useAuth'
import { useAuthStore } from '@/stores/auth.store'
import { type Theme, useUIStore } from '@/stores/ui.store'
import { Kbd, META_KEY } from '@/components/ui/kbd'
import { useNavigate } from '@tanstack/react-router'
import {
  ChevronDown,
  Lock,
  LogOut,
  Menu,
  Moon,
  Search,
  Settings,
  Sun,
  UserCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { openCommandPalette } from './CommandPalette'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'

export function Header() {
  const { t } = useTranslation(['common', 'auth'])
  const org = useAuthStore((s) => s.org)
  const user = useAuthStore((s) => s.user)
  const theme = useUIStore((s) => s.theme)
  const setTheme = useUIStore((s) => s.setTheme)
  const openMobileSidebar = useUIStore((s) => s.openMobileSidebar)
  const { logout } = useAuth()
  const navigate = useNavigate()

  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-color-scheme: dark)').matches)
  const nextTheme: Theme = isDark ? 'light' : 'dark'

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '—'

  const menuItems = user
    ? [
        {
          label: (
            <span className="flex items-center gap-2">
              <UserCircle className="h-4 w-4" />
              {t('common:user_menu.profile')}
            </span>
          ),
          onSelect: () => void navigate({ to: '/settings/profile' }),
        },
        {
          label: (
            <span className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              {t('common:user_menu.preferences')}
            </span>
          ),
          onSelect: () => void navigate({ to: '/settings/preferences' }),
        },
        {
          label: (
            <span className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              {t('common:user_menu.security')}
            </span>
          ),
          onSelect: () => void navigate({ to: '/settings/security' }),
        },
        {
          label: (
            <span className="flex items-center gap-2">
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {isDark ? t('common:user_menu.light_mode') : t('common:user_menu.dark_mode')}
            </span>
          ),
          onSelect: () => setTheme(nextTheme),
        },
        {
          label: (
            <span className="flex items-center gap-2 text-muted-foreground">
              <LogOut className="h-4 w-4" />
              {t('common:user_menu.signout')}
            </span>
          ),
          onSelect: () => logout.mutate(),
          destructive: true,
        },
      ]
    : []

  return (
    <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-border bg-header-background/80 px-4 backdrop-blur md:px-6">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={openMobileSidebar}
          aria-label={t('common:open_menu')}
        >
          <Menu className="h-5 w-5" />
        </Button>

        {org ? (
          <Badge variant="info" withDot className="hidden max-w-[200px] truncate lg:inline-flex">
            <span className="truncate">{org.name}</span>
          </Badge>
        ) : null}

        <div className="mx-1 hidden h-5 w-px bg-muted lg:block" />

        <WorkspaceSwitcher />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={openCommandPalette}
          className="flex h-9 items-center gap-2 rounded-xl border border-border bg-muted/50 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={t('common:command.title', 'Recherche & actions')}
        >
          <Search className="h-4 w-4" />
          <span className="hidden lg:inline">{t('common:command.search', 'Rechercher')}</span>
          <Kbd className="hidden lg:inline-flex">{META_KEY}K</Kbd>
        </button>

        <NotificationBell />

        {user ? (
          <Dropdown
            align="end"
            trigger={
              <div className="flex items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-muted">
                <UserAvatar user={user} size="sm" />
                <div className="hidden flex-col items-start leading-tight md:flex">
                  <span className="text-sm font-medium text-foreground">{displayName}</span>
                  {org ? (
                    <span className="max-w-[160px] truncate text-xs text-muted-foreground">
                      {org.name}
                    </span>
                  ) : null}
                </div>
                <ChevronDown className="hidden h-4 w-4 text-muted-foreground md:block" />
              </div>
            }
            items={menuItems}
          />
        ) : null}
      </div>
    </header>
  )
}
